import api from '../lib/api';

const API_URL = import.meta.env.VITE_APPOINTMENTS_API_URL;

// ---------------------------------------------------------------------
//  TYPES
// ---------------------------------------------------------------------
export interface AppointmentPayload {
  clientName: string;
  email: string;
  phone?: string;
  address?: string;

  clientCompany?: string;
  industry?: string;
  companyEmail?: string;
  companyPhone?: string;

  notes?: string; // free-text ONLY

  appointmentDate: string;
  slot?: string;
  slotId?: string;
  meetingLink?: string;
}

export interface AppointmentUpdatePayload extends AppointmentPayload {}

export interface ReschedulePayload {
  appointmentDate: string;
  slotId?: string;
  slot?: string;
}

export interface Appointment {
  _id: string;
  clientName: string;
  email: string;
  phone?: string;
  address?: string;
  clientCompany?: string;
  industry?: string;
  companyEmail?: string;
  companyPhone?: string;
  notes?: string;
  appointmentDate: string;
  startDateTime: string;
  endDateTime: string;
  company: string;
  slot?: string;
  meetingType?: 'online' | 'onsite';
  meetingLink?: string;
  isConflict?: boolean;
  createdAt: string;
}

// ---------------------------------------------------------------------
//  MAIN SERVICE FUNCTIONS
// ---------------------------------------------------------------------

/**
 * Get all appointments for a company
 */
export async function fetchAppointments(company: string) {
  try {
    const res = await api.get(`${API_URL}/api/${company}/appointments`);
    return res.data;
  } catch (err) {
    console.error('Error fetching appointments:', err);
    throw err;
  }
}

/**
 * Create a new appointment
 */
export async function createAppointment(
  company: string,
  payload: AppointmentPayload,
) {
  try {
    const res = await api.post(
      `${API_URL}/api/${company}/appointments`,
      payload,
    );
    return res.data;
  } catch (err) {
    console.error('Error creating appointment:', err);
    throw err;
  }
}

/**
 * Update appointment (Admin full edit)
 */
export async function updateAppointment(
  company: string,
  id: string,
  payload: AppointmentUpdatePayload,
) {
  try {
    const res = await api.put(
      `${API_URL}/api/${company}/appointments/${id}`,
      payload,
    );
    return res.data;
  } catch (err) {
    console.error('Error updating appointment:', err);
    throw err;
  }
}

/**
 * Delete an appointment
 */
export async function deleteAppointment(company: string, id: string) {
  try {
    const res = await api.delete(
      `${API_URL}/api/${company}/appointments/${id}`,
    );
    return res.data;
  } catch (err) {
    console.error('Error deleting appointment:', err);
    throw err;
  }
}

/**
 * Join meeting using secure redirect token
 */
export async function joinMeeting(token: string) {
  try {
    const res = await api.get(`${API_URL}/api/join?token=${token}`, {
      maxRedirects: 0, // prevent browser auto-follow if desired
      validateStatus: () => true,
    });
    return res;
  } catch (err) {
    console.error('Error joining meeting:', err);
    throw err;
  }
}

/**
 * Reschedule an existing appointment to a new date and slot
 */
export async function rescheduleAppointment(
  company: string,
  id: string,
  payload: ReschedulePayload,
) {
  try {
    const res = await api.patch(
      `${API_URL}/api/${company}/appointments/${id}/reschedule`,
      payload,
    );
    return res.data;
  } catch (err) {
    console.error('Error rescheduling appointment:', err);
    throw err;
  }
}
